"use client";

import { Bars2Icon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";

import AuthButton from "@/components/nav/AuthButton";
import MobileNavMenu from "@/components/nav/MobileNavMenu";
import NavDropdown from "@/components/nav/NavDropdown";
import NavSearchDialog from "@/components/NavSearchDialog";
import SimulatorPeekNav from "@/components/nav/SimulatorPeekNav";
import TrainingModeNav from "@/components/nav/TrainingModeNav";
import WorkspaceSwitcher from "@/components/nav/WorkspaceSwitcher";
import { NavBarProvider } from "@/components/nav/NavBarContext";
import { useStudioBranding } from "@/components/nav/useStudioBranding";
import { useStudioNavData } from "@/components/nav/useStudioNavData";
import { useWorkspaceSwitcher } from "@/components/nav/useWorkspaceSwitcher";
import {
  buildNavSections,
  createNavContext,
  hasVisibleNavItems,
  resolveActiveNavKey,
} from "@/lib/navigation/nav-items";
import { stripNavigationLocalePrefix } from "@/lib/navigation/is-nav-item-active";
import { isSimulatorRoute } from "@/lib/simulatorRoutes";
import { isTrainingModeRestrictedUser } from "@/lib/training-mode-users";
import { cn } from "@/lib/utils";
import logoOrange from "../public/logo-orange.png";

/**
 * The top navigation bar.
 *
 * What the menus contain is declared once in `lib/navigation/nav-manifest.js`
 * and resolved for this viewer by `lib/navigation/nav-items.js`. This file is
 * the composition root: it gathers the session, studio and module state those
 * need, then arranges the pieces (switcher, avatar, dropdowns, mobile drawer).
 * Adding a nav link belongs in the manifest, not here.
 */
export default function NavMenu() {
  const { data: session, update } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const normalizedPathname = stripNavigationLocalePrefix(pathname || "");
  // Collapse the nav into a hover-to-expand peek bar while the user is in a
  // live simulation, so laptops get more vertical room for the conversation.
  const isSimulator = isSimulatorRoute(normalizedPathname);

  const [currentHash, setCurrentHash] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const updateCurrentHash = () => setCurrentHash(window.location.hash.slice(1));

    updateCurrentHash();
    window.addEventListener("hashchange", updateCurrentHash);
    window.addEventListener("popstate", updateCurrentHash);
    return () => {
      window.removeEventListener("hashchange", updateCurrentHash);
      window.removeEventListener("popstate", updateCurrentHash);
    };
  }, [pathname]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const { displayName, displayLogo } = useStudioBranding({
    session,
    searchParams,
    normalizedPathname,
  });
  const { enabledModules, isCommunityLeader, modules } =
    useStudioNavData(session);
  const {
    availableWorkspaces,
    handleWorkspaceSwitch,
    isSwitchingWorkspace,
    selectedWorkspaceId,
    switcherCurrentWorkspace,
  } = useWorkspaceSwitcher({ session, update });

  const userData = session?.user;
  const workspace = session?.workspace;
  const isSuperUser = session?.user?.superUser;
  const isAdmin = session?.user?.role === "admin";
  const createLocaleUrl = (path) => path;

  const navSections = buildNavSections(
    createNavContext({
      user: userData,
      workspace,
      enabledModules,
      modules,
      isCommunityLeader,
    }),
  );

  // Exactly one menu is highlighted, chosen by the most specific matching
  // prefix — several menus reach into each other's route trees.
  const activeMenuKey = resolveActiveNavKey(navSections, normalizedPathname);

  // The mobile drawer shows the same menus as accordions. Empty ones are
  // dropped so a header never appears with no links beneath it.
  const mobileSections = navSections
    .map((menu) => ({
      label: menu.menuLabel,
      subtitle: menu.subtitle,
      items: menu.items,
    }))
    .filter((section) => hasVisibleNavItems(section.items));

  if (isTrainingModeRestrictedUser(session?.user)) {
    return (
      <TrainingModeNav
        session={session}
        normalizedPathname={normalizedPathname}
        displayLogo={displayLogo}
        displayName={displayName}
      />
    );
  }

  return (
    <SimulatorPeekNav enabled={isSimulator}>
      <div>
        {/* Cream bg matches AdminSideNav's rail (--sal-surface-chrome) so the NavMenu and
            sidebar form one continuous cream surface at the top-left corner.

            Outer flex has three children:
              1. Logo column (md:w-[15.5rem]) — visually sits over the sidebar
                 column. Width = sidebar (16rem) + ml-6 gap (1.5rem) − container
                 padding (2rem) = 15.5rem, so its right edge lands exactly where
                 the content column's left edge does.
              2. AuthButton — sits flush at the content column's left edge.
              3. Nav links (right group) — pushed to the right via ml-auto. */}
        <nav className="bg-surface-chrome">
          <div className="container flex flex-nowrap items-center px-4 py-2.5 mx-auto gap-2 sm:gap-3 md:gap-0">
            <div
              className={cn(
                "flex items-center min-w-0 shrink-0",
                // The fixed width + clip only exists to line the logo up with
                // the admin sidebar rail, which renders only for signed-in
                // users. Signed-out pages (sign-in, public share pages) have no
                // sidebar, so let the company name show in full instead of
                // truncating.
                !!session && "md:w-[15.5rem] md:overflow-hidden",
              )}
            >
              {(isSuperUser || isAdmin) && session ? (
                <WorkspaceSwitcher
                  currentWorkspace={switcherCurrentWorkspace}
                  workspaces={availableWorkspaces}
                  value={selectedWorkspaceId}
                  isSwitching={isSwitchingWorkspace}
                  onSelect={handleWorkspaceSwitch}
                />
              ) : session?.workspace?.image ? (
                <Image
                  src={session?.workspace?.image}
                  alt={`${session?.workspace?.name} Logo`}
                  width={200}
                  height={200}
                  className="h-10 w-24 object-contain sm:h-12 sm:w-40"
                  priority
                />
              ) : (
                <>
                  <Image
                    src={displayLogo || logoOrange}
                    alt={displayName ? `${displayName} Logo` : "Salesably Logo"}
                    width={160}
                    height={40}
                    className="h-10 w-24 object-contain sm:h-12 sm:w-40"
                    priority
                  />
                  <div
                    className={cn(
                      "hidden ml-2 text-lg font-bold text-gray-700 md:block",
                      !!session && "truncate",
                    )}
                  >
                    {displayName ||
                      "Salesably — Execution Excellence for Sales Teams"}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center shrink-0 ml-2 md:ml-0">
              <AuthButton
                userData={userData}
                session={session}
                createLocaleUrl={createLocaleUrl}
              />
            </div>

            {session && (
              <NavBarProvider>
                <div className="ml-auto flex items-center gap-2">
                  {/* Desktop Menu */}
                  <div className="items-center hidden ml-auto space-x-8 lg:flex">
                    <Link
                      href={createLocaleUrl("/")}
                      className={`relative text-sm font-medium text-gray-700 transition-colors duration-150 hover:text-brand-800 ${
                        normalizedPathname === "/"
                          ? "text-brand-800"
                          : ""
                      }`}
                    >
                      Home
                    </Link>
                    {navSections.map((menu) => (
                      <NavDropdown
                        key={menu.key}
                        label={menu.menuLabel}
                        subtitle={menu.subtitle}
                        items={menu.items}
                        currentHash={currentHash}
                        onNavigateHash={setCurrentHash}
                        crossLink={menu.crossLink}
                        isActive={menu.key === activeMenuKey}
                      />
                    ))}
                  </div>
                  <NavSearchDialog />
                  {/* Mobile Menu Button */}
                  <button
                    className="rounded-md p-2 text-gray-700 transition-colors hover:bg-stone-200/60 lg:hidden"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Toggle menu"
                    aria-expanded={isMobileMenuOpen}
                  >
                    <Bars2Icon className="w-6 h-6" />
                  </button>
                  {/* Mobile Menu — slide-in drawer with collapsible sections */}
                  <MobileNavMenu
                    open={isMobileMenuOpen}
                    onClose={() => setIsMobileMenuOpen(false)}
                    pathname={pathname}
                    currentHash={currentHash}
                    onNavigateHash={setCurrentHash}
                    sections={mobileSections}
                    createLocaleUrl={createLocaleUrl}
                  />
                </div>
              </NavBarProvider>
            )}
          </div>
        </nav>
      </div>
    </SimulatorPeekNav>
  );
}
